import { AnyAction, Reducer } from "redux";
import type { TaskStatuses } from "api/services/tasks/models";

import { 
    SET_NOTIFICATION_TABLE_HISTORY_DATA, 
    SET_NOTIFICATION_TABLE_HISTORY_ERROR,
    SET_NOTIFICATION_TABLE_HISTORY_LOADING,
    SET_CURRENT_HISTORY_NOTIFICATION,
    SET_CURRENT_HISTORY_NOTIFICATION_LOADING,
    SET_CURRENT_HISTORY_NOTIFICATION_ERROR,
    SET_HISTORY_NOTIFICATION_TABLE_PARAMS,
    SET_NOTIFICATION_HISTORY_DROPDOWN,
    SET_NOTIFICATION_HISTORY_DROPDOWN_ERROR,
    SET_LAST_DROPDOWN_NOTIFICATION_ID
} from "./constants";

export interface IDefaultNotification {
    title: string,
    description?: string,
    createdAt: string,
    type: string,
    iconType: string,
    id: string
}

export interface IRequestParams {
    [key: string]: string | number,
    limit: number,
    page: number
}

export interface INotificationHistory {
    title: string,
    createdAt: string,
    status: TaskStatuses,
    id: number,
    progress: number,
    updatedAt: string
}

export type INotifications = Array<IDefaultNotification | IActionNotification | IDropdownNotification>

export interface IActionNotification extends IDefaultNotification {
    extraData: {
        data: {
            createdAt: string
        },
        localStorageName?: string
    }
}

export interface IDropdownNotification extends IDefaultNotification {
    extraData: {
        data: object,
        status: string
    }
}

export interface IHistoryCurrentNotification {
    data: IHistoryCurrentNotificationData | null,
    isLoading: boolean,
    error: string | null
}

export interface ITableHistoryNotifications {
    data: {
        data: INotificationHistory[] | null,
        total: number
    } | null,
    isLoading: boolean,
    error: string | null,
    params: IRequestParams
}

export interface IHistoryNotificationDropdown {
    data: INotificationHistory[] | null,
    isLoading: boolean,
    error: string | null,
    params: {[key:string]: number | string},
    lastNotificationId: number | null
}

interface IProgressData {
    [key: string]: any
}

export interface IHistoryCurrentNotificationData {
    createdAt: string,
    createdBy: number,
    updatedAt: number,
    id: number,
    name: string,
    progress: number,
    status: number,
    type: number,
    result: string,
    error: string
    progressData: IProgressData
}

interface INotificationReducer {
    temporaryNotifications: {
        notifications: INotifications
    },
    notificationsHistory: {
        table: ITableHistoryNotifications,
        current: IHistoryCurrentNotification,
        dropdown: IHistoryNotificationDropdown,
    }
}

const initialState = {
    temporaryNotifications: {
        notifications: []
    },
    notificationsHistory: {
        table: {
            data: null,
            isLoading: false,
            error: null,
            params: {
                limit: 10,
                page: 1,
                locale: ""
            }
        },
        current: {
            data: null,
            isLoading: false,
            error: null
        },
        dropdown: {
            data: null,
            params: {
                limit: 6,
                page: 1,
                locale: ""
            },
            isLoading: false,
            error: null,
            lastNotificationId: null
        }
    }
}

const notifications: Reducer<INotificationReducer, AnyAction> = (state = initialState, action): INotificationReducer => {
    switch (action.type) {

        case SET_LAST_DROPDOWN_NOTIFICATION_ID: {
            return {
                ...state,
                notificationsHistory: {
                    ...state.notificationsHistory,
                    dropdown: {
                        ...state.notificationsHistory.dropdown,
                        lastNotificationId: action.payload
                    }
                }
            }
        }

        case SET_NOTIFICATION_HISTORY_DROPDOWN: {
            return {
                ...state,
                notificationsHistory: {
                    ...state.notificationsHistory,
                    dropdown: {
                        ...state.notificationsHistory.dropdown,
                        data: action.payload
                    }
                }
            }
        }

        case SET_NOTIFICATION_HISTORY_DROPDOWN_ERROR: {
            return {
                ...state,
                notificationsHistory: {
                    ...state.notificationsHistory,
                    dropdown: {
                        ...state.notificationsHistory.dropdown,
                        error: action.payload
                    }
                }
            }
        }

        case SET_CURRENT_HISTORY_NOTIFICATION: {
            return {
                ...state,
                notificationsHistory: {
                    ...state.notificationsHistory,
                    current: {
                        ...state.notificationsHistory.current,
                        data: action.payload
                    }
                }
            }
        }

        case SET_CURRENT_HISTORY_NOTIFICATION_LOADING: {
            return {
                ...state,
                notificationsHistory: {
                    ...state.notificationsHistory,
                    current: {
                        ...state.notificationsHistory.current,
                        isLoading: action.payload
                    }
                }
            }
        }

        case SET_CURRENT_HISTORY_NOTIFICATION_ERROR: {
            return {
                ...state,
                notificationsHistory: {
                    ...state.notificationsHistory,
                    current: {
                        ...state.notificationsHistory.current,
                        error: action.payload
                    }
                }
            }
        }

        case SET_NOTIFICATION_TABLE_HISTORY_DATA: {
            return {
                ...state,
                notificationsHistory: {
                    ...state.notificationsHistory,
                    table: {
                        ...state.notificationsHistory.table,
                        data: action.payload,
                        error: null
                    }
                }
            }
        }

        case SET_NOTIFICATION_TABLE_HISTORY_LOADING: {
            return {
                ...state,
                notificationsHistory: {
                    ...state.notificationsHistory,
                    table: {
                        ...state.notificationsHistory.table,
                        isLoading: action.payload
                    }
                }
            }
        }

        case SET_NOTIFICATION_TABLE_HISTORY_ERROR: {
            return {
                ...state,
                notificationsHistory: {
                    ...state.notificationsHistory,
                    table: {
                        ...state.notificationsHistory.table,
                        error: action.payload
                    }
                }
            }
        }

        case SET_HISTORY_NOTIFICATION_TABLE_PARAMS: {
            return {
                ...state,
                notificationsHistory: {
                    ...state.notificationsHistory,
                    table: {
                        ...state.notificationsHistory.table,
                        params: action.payload
                    }
                }
            }
        }

        case "SET_TEMPORARY_NOTIFICATIONS": {
            return {
                ...state,
                temporaryNotifications: {
                    notifications: [...state.temporaryNotifications.notifications, action.payload]
                }
            }
        }

        case "DELETE_TEMPORARY_NOTIFICATION": {

            let newTemporaryNotifications = [...state.temporaryNotifications.notifications];
            newTemporaryNotifications = newTemporaryNotifications.filter(data => data.id !== action.payload.id);

            return {
                ...state,
                temporaryNotifications: {
                    notifications: newTemporaryNotifications
                }
            }
        }

        default: return state;
    }
}

export default notifications;